import { Camera, FastForward, Info, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/ui/foundation/primitives/button.js';
import { Card, CardContent } from '@/ui/foundation/primitives/card.js';
import { Label } from '@/ui/foundation/primitives/label.js';
import { Separator } from '@/ui/foundation/primitives/separator.js';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/ui/foundation/primitives/tabs.js';
import type { MatchUiBridgeV2, MatchUiSnapshotV2 } from '@/ui/runtime/contracts/match-ui-v2.js';
import type { ReactNode } from 'react';

interface MatchUiAppProps {
    bridge: MatchUiBridgeV2;
}

const SPEED_STEPS: Array<1 | 2 | 4 | 8 | 16 | 32> = [1, 2, 4, 8, 16, 32];
const TACTIC_STEP_LABELS = {
    passLength: ['Very Short', 'Short', 'Mixed', 'Direct', 'Long'],
    mentality: ['Very Defensive', 'Defensive', 'Balanced', 'Attacking', 'All-Out'],
    pressing: ['Drop Off', 'Conservative', 'Balanced', 'Aggressive', 'Relentless'],
    possession: ['Fast Break', 'Mixed', 'Balanced', 'Patient', 'Control']
} as const;

export function MatchUiApp({ bridge }: MatchUiAppProps) {
    const [snapshot, setSnapshot] = useState<MatchUiSnapshotV2>(bridge.getSnapshot());
    const [subOut, setSubOut] = useState<string>('');
    const [subIn, setSubIn] = useState<string>('');
    const [panelOpen, setPanelOpen] = useState<boolean>(false);
    const commands = useMemo(() => bridge.commands, [bridge]);

    useEffect(() => bridge.subscribe(setSnapshot), [bridge]);

    useEffect(() => {
        if (!subOut && snapshot.substitutions.onField.length > 0) {
            setSubOut(String(snapshot.substitutions.onField[0].id));
        }
        if (!subIn && snapshot.substitutions.bench.length > 0) {
            setSubIn(String(snapshot.substitutions.bench[0].id));
        }
    }, [subOut, subIn, snapshot.substitutions.bench, snapshot.substitutions.onField]);

    const speedIndex = SPEED_STEPS.indexOf(snapshot.controls.speed);
    const nextSpeed = SPEED_STEPS[(speedIndex + 1) % SPEED_STEPS.length];
    const playPauseLabel = snapshot.controls.canStart ? 'Start' : (snapshot.controls.paused ? 'Resume' : 'Pause');

    return (
        <div className="pointer-events-none absolute inset-0 z-20">
            <div className="pointer-events-auto absolute left-1/2 top-4 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/30 bg-black/70 px-3 py-1.5 text-white shadow-lg backdrop-blur">
                <div className="min-w-[9.5rem] text-right text-xs font-semibold uppercase tracking-wide text-white/90">
                    {snapshot.scoreboard.homeTeamName}
                </div>
                <div className="rounded-md bg-white/12 px-2 py-1 text-sm font-bold tabular-nums">
                    {snapshot.scoreboard.homeScore} - {snapshot.scoreboard.awayScore}
                </div>
                <div className="min-w-[9.5rem] text-xs font-semibold uppercase tracking-wide text-white/90">
                    {snapshot.scoreboard.awayTeamName}
                </div>
                <div className="ml-1 w-[6.25rem] rounded-md bg-amber-400/20 px-2 py-1 text-center text-xs font-bold text-amber-200 tabular-nums">
                    {snapshot.scoreboard.timeText}
                </div>
            </div>

            <div className="pointer-events-auto absolute bottom-4 left-4 flex items-center gap-2">
                <RoundIconButton onClick={() => void commands.togglePauseOrStart()} ariaLabel={playPauseLabel}>
                    {snapshot.controls.canStart || snapshot.controls.paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                </RoundIconButton>
                <RoundIconButton onClick={() => void commands.cycleSpeed()} ariaLabel={`Speed x${nextSpeed}`}>
                    <FastForward className="h-4 w-4" />
                    <span className="absolute -bottom-1.5 -right-1.5 rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">x{nextSpeed}</span>
                </RoundIconButton>
                <RoundIconButton onClick={() => void commands.toggleAudio()} ariaLabel={snapshot.controls.muted ? 'Unmute' : 'Mute'}>
                    {snapshot.controls.muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </RoundIconButton>
                <RoundIconButton onClick={() => void commands.cycleCamera()} ariaLabel={`Camera ${snapshot.controls.cameraMode}`}>
                    <Camera className="h-4 w-4" />
                </RoundIconButton>
            </div>

            <div className="pointer-events-auto absolute right-4 top-4">
                <RoundIconButton onClick={() => setPanelOpen((open) => !open)} ariaLabel={panelOpen ? 'Close match panel' : 'Open match panel'}>
                    <Info className="h-4 w-4" />
                </RoundIconButton>
            </div>

            {panelOpen ? (
                <Card className="pointer-events-auto absolute right-4 top-16 w-[min(30rem,calc(100vw-2rem))] border-white/20 bg-background/95 shadow-2xl backdrop-blur">
                    <CardContent className="px-4 py-3">
                        <Tabs defaultValue="tactics" className="flex min-h-0 flex-col gap-3">
                            <TabsList className="grid grid-cols-3">
                                <TabsTrigger value="tactics">Tactics</TabsTrigger>
                                <TabsTrigger value="players">Players</TabsTrigger>
                                <TabsTrigger value="stats">Stats</TabsTrigger>
                            </TabsList>
                            <TabsContent value="tactics" className="flex flex-col gap-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex flex-col gap-1">
                                        <Label htmlFor="match-formation">Formation</Label>
                                        <select
                                            id="match-formation"
                                            className="h-9 rounded-md border bg-background px-3 text-sm"
                                            value={snapshot.tactics.formation}
                                            onChange={(event) => void commands.setFormation(event.target.value)}
                                        >
                                            <option value="4-4-2">4-4-2</option>
                                            <option value="4-3-3">4-3-3</option>
                                            <option value="3-5-2">3-5-2</option>
                                            <option value="5-3-2">5-3-2</option>
                                        </select>
                                    </div>
                                </div>
                                <TacticSlider
                                    label="Pass Style"
                                    value={snapshot.tactics.passLength}
                                    steps={TACTIC_STEP_LABELS.passLength}
                                    onChange={(value) => void commands.setTactic('passLength', value)}
                                />
                                <TacticSlider
                                    label="Mentality"
                                    value={snapshot.tactics.mentality}
                                    steps={TACTIC_STEP_LABELS.mentality}
                                    onChange={(value) => void commands.setTactic('mentality', value)}
                                />
                                <TacticSlider
                                    label="Pressing"
                                    value={snapshot.tactics.pressing}
                                    steps={TACTIC_STEP_LABELS.pressing}
                                    onChange={(value) => void commands.setTactic('pressing', value)}
                                />
                                <TacticSlider
                                    label="Possession"
                                    value={snapshot.tactics.possession}
                                    steps={TACTIC_STEP_LABELS.possession}
                                    onChange={(value) => void commands.setTactic('possession', value)}
                                />
                            </TabsContent>
                            <TabsContent value="players" className="flex max-h-[52vh] min-h-0 flex-col gap-3">
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                    <span>{snapshot.managedPlayersTitle}</span>
                                    <span>Subs {snapshot.substitutions.used}/{snapshot.substitutions.max}</span>
                                </div>
                                <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto pr-1">
                                    {snapshot.managedPlayers.map((player) => (
                                        <div key={player.id} className="rounded-md border bg-card p-2">
                                            <div className="flex items-center justify-between gap-2">
                                                <div className="truncate text-sm font-medium">{player.name} <span className="text-muted-foreground">{player.role}</span></div>
                                                <div className="flex items-center gap-1">
                                                    <Button size="sm" variant={player.highlighted ? 'default' : 'outline'} onClick={() => void commands.togglePlayerHighlight(player.id)}>Target</Button>
                                                    <Button size="sm" variant="secondary" onClick={() => void commands.togglePlayerDetails(player.id)}>{player.expanded ? 'Hide' : 'Details'}</Button>
                                                </div>
                                            </div>
                                            {player.expanded ? (
                                                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                                                    <AttributePips label="PAC" value={player.attributes.pace} />
                                                    <AttributePips label="BAL" value={player.attributes.ballControl} />
                                                    <AttributePips label="PAS" value={player.attributes.passing} />
                                                    <AttributePips label="TAC" value={player.attributes.tackling} />
                                                    <AttributePips label="END" value={player.attributes.endurance} />
                                                    <AttributePips label="POS" value={player.attributes.positioning} />
                                                    <AttributePips label="SHT" value={player.attributes.shotPower} />
                                                    <AttributePips label="REA" value={player.attributes.reactions} />
                                                    <AttributePips label="CMP" value={player.attributes.composure} />
                                                    <AttributePips label="JMP" value={player.attributes.jumping} />
                                                    <AttributePips label="MOR" value={player.attributes.morale} />
                                                    <AttributePips label="GK" value={player.attributes.goalkeeping} />
                                                </div>
                                            ) : null}
                                        </div>
                                    ))}
                                </div>
                                <Separator />
                                <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-2">
                                    <div className="flex flex-col gap-1">
                                        <Label htmlFor="sub-out">Out</Label>
                                        <select id="sub-out" className="h-9 rounded-md border bg-background px-3 text-sm" value={subOut} onChange={(event) => setSubOut(event.target.value)}>
                                            {snapshot.substitutions.onField.map((player) => (
                                                <option key={player.id} value={String(player.id)}>{player.name} ({player.role})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        <Label htmlFor="sub-in">In</Label>
                                        <select id="sub-in" className="h-9 rounded-md border bg-background px-3 text-sm" value={subIn} onChange={(event) => setSubIn(event.target.value)}>
                                            {snapshot.substitutions.bench.map((player) => (
                                                <option key={player.id} value={String(player.id)}>{player.name} ({player.role})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <Button
                                        size="sm"
                                        disabled={!snapshot.substitutions.canSubNow}
                                        onClick={() => {
                                            const outId = Number(subOut);
                                            const inId = Number(subIn);
                                            if (Number.isFinite(outId) && Number.isFinite(inId)) {
                                                void commands.makeSubstitution(outId, inId);
                                            }
                                        }}
                                    >
                                        Sub
                                    </Button>
                                </div>
                            </TabsContent>
                            <TabsContent value="stats" className="flex flex-col gap-2 text-sm">
                                <StatLine label="Possession" home={`${snapshot.stats.possession.home}%`} away={`${snapshot.stats.possession.away}%`} />
                                <StatLine label="Territory" home={`${snapshot.stats.territory.home}%`} away={`${snapshot.stats.territory.away}%`} />
                                <StatLine label="Shots" home={String(snapshot.stats.shots.home)} away={String(snapshot.stats.shots.away)} />
                                <StatLine label="Tackles Won" home={String(snapshot.stats.tacklesWon.home)} away={String(snapshot.stats.tacklesWon.away)} />
                                <StatLine label="Corners" home={String(snapshot.stats.corners.home)} away={String(snapshot.stats.corners.away)} />
                                <StatLine label="Final Third Entries" home={String(snapshot.stats.finalThirdEntries.home)} away={String(snapshot.stats.finalThirdEntries.away)} />
                            </TabsContent>
                        </Tabs>
                    </CardContent>
                </Card>
            ) : null}

            {snapshot.isFullTime ? (
                <div className="pointer-events-auto absolute left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2">
                    <Button
                        size="lg"
                        className="h-16 min-w-[20rem] rounded-xl px-8 text-lg font-bold shadow-2xl"
                        onClick={() => void commands.returnToHub()}
                    >
                        Return To Manager View
                    </Button>
                </div>
            ) : null}

            <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2">
                <div className="rounded-md bg-black/65 px-4 py-1.5 text-center text-sm text-white/95 shadow">
                    {snapshot.scoreboard.phaseText}
                </div>
            </div>

            <div className="pointer-events-none absolute bottom-4 right-4 rounded-md bg-black/70 px-2 py-1 font-mono text-xs text-lime-300 shadow">
                {snapshot.scoreboard.fps} FPS
            </div>
        </div>
    );
}

function RoundIconButton(props: { onClick: () => void; ariaLabel: string; children: ReactNode }) {
    return (
        <Button
            size="icon"
            variant="outline"
            className="relative h-10 w-10 rounded-full border-white/30 bg-black/65 text-white hover:bg-black/80"
            aria-label={props.ariaLabel}
            onClick={props.onClick}
        >
            {props.children}
        </Button>
    );
}

function AttributePips(props: { label: string; value: number }) {
    const tone = getRatingTone(props.value);
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground">{props.label}</span>
            <div className="flex items-center gap-1">
                {Array.from({ length: 5 }, (_entry, index) => {
                    const units = Math.max(0, Math.min(10, Math.round(props.value / 10)));
                    const fullThreshold = (index + 1) * 2;
                    const halfThreshold = fullThreshold - 1;
                    const fill = units >= fullThreshold ? 1 : (units === halfThreshold ? 0.5 : 0);
                    return (
                        <span
                            key={index}
                            className={
                                `match-rating-pip relative h-2.5 w-2.5 overflow-hidden rounded-sm bg-muted match-rating-pip--${tone} ` +
                                (fill === 1 ? "match-rating-pip--full" : fill === 0.5 ? "match-rating-pip--half" : "match-rating-pip--empty")
                            }
                        >
                            <span className="match-rating-pip__fill absolute inset-y-0 left-0 rounded-sm" />
                        </span>
                    );
                })}
            </div>
        </div>
    );
}

function getRatingTone(value: number): "low" | "mid-low" | "mid" | "high" {
    if (value < 40) {
        return "low";
    }
    if (value < 60) {
        return "mid-low";
    }
    if (value < 75) {
        return "mid";
    }
    return "high";
}

function TacticSlider(props: {
    label: string;
    value: number;
    steps: readonly string[];
    onChange: (value: number) => void;
}) {
    return (
        <div className="flex flex-col gap-1.5 rounded-md border border-border/70 bg-card/70 p-2">
            <div className="flex items-center justify-between text-xs">
                <span className="font-semibold">{props.label}</span>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-primary">{props.steps[props.value] ?? props.value}</span>
            </div>
            <input
                type="range"
                min={0}
                max={4}
                step={1}
                value={props.value}
                className="match-tactic-slider w-full"
                onChange={(event) => props.onChange(Number(event.target.value))}
            />
            <div className="grid grid-cols-5 gap-1 text-[10px] text-muted-foreground">
                {props.steps.map((step, index) => (
                    <span key={step} className={index === props.value ? 'font-semibold text-foreground' : ''}>{step}</span>
                ))}
            </div>
        </div>
    );
}

function StatLine(props: { label: string; home: string; away: string }) {
    return (
        <div className="grid grid-cols-[3rem_1fr_3rem] items-center gap-2">
            <span className="text-right font-semibold">{props.home}</span>
            <span className="text-center text-xs uppercase tracking-wide text-muted-foreground">{props.label}</span>
            <span className="font-semibold">{props.away}</span>
        </div>
    );
}
